import type { Attack, Attackable, AttackerGroup, AttackerToBlock, Block, BlockerGroup, BlockOptions, DamageAssignment, PublicState } from "../protocol";
import { livePlayers, optionValue } from "./game-text";

// Declaring attackers and blockers on the board: the plan the viewer is building, checked against the
// engine's relations. Creatures alike are one group there: each attacker group lists the players and
// planeswalkers its creatures may attack, and each blocker group the attackers its creatures may block.
// Each relation is indexed by creature once, so a big board's declaration is a lookup a click.

// What an attacker attacks: a player, or one of their planeswalkers (506.2).
export type Defender = { pid: string; planeswalker: number | null };

export const defenderOf = (a: Attack): Defender => ({ pid: a.defender, planeswalker: optionValue(a.planeswalker) });

const attacked = (t: Attackable): Defender => ({ pid: t.defender, planeswalker: optionValue(t.planeswalker) });

const sameDefender = (a: Defender, b: Defender): boolean => a.pid === b.pid && a.planeswalker === b.planeswalker;

const attackIndexes = new WeakMap<AttackerGroup[], Map<number, AttackerGroup>>();

function attackerGroup(groups: AttackerGroup[], attacker: number): AttackerGroup | undefined {
  let index = attackIndexes.get(groups);
  if (!index) {
    index = new Map(groups.flatMap((g) => g.attackers.map((oid) => [oid, g] as const)));
    attackIndexes.set(groups, index);
  }
  return index.get(attacker);
}

export function canAttack(groups: AttackerGroup[], attacker: number): boolean {
  return attackerGroup(groups, attacker) !== undefined;
}

export function canAttackAt(groups: AttackerGroup[], attacker: number, to: Defender): boolean {
  return attackerGroup(groups, attacker)?.may_attack.some((t) => sameDefender(attacked(t), to)) ?? false;
}

// A player or planeswalker some creature may attack.
export function isAttackable(groups: AttackerGroup[], to: Defender): boolean {
  return groups.some((g) => g.may_attack.some((t) => sameDefender(attacked(t), to)));
}

export function isAttackablePlayer(groups: AttackerGroup[], pid: string): boolean {
  return groups.some((g) => g.may_attack.some((t) => t.defender === pid));
}

export function isAttackablePlaneswalker(groups: AttackerGroup[], oid: number): boolean {
  return groups.some((g) => g.may_attack.some((t) => optionValue(t.planeswalker) === oid));
}

// What `attacker` may attack at `to`, else the first it may.
function attackFor(groups: AttackerGroup[], attacker: number, to: Defender | null): Attack | null {
  const options = attackerGroup(groups, attacker)?.may_attack ?? [];
  const at = (to ? options.find((t) => sameDefender(attacked(t), to)) : undefined) ?? options[0];
  return at ? { attacker, defender: at.defender, planeswalker: at.planeswalker } : null;
}

// 508.1b: an attacker attacks one player or planeswalker, so a new one replaces its last.
export function withAttack(plan: Attack[], groups: AttackerGroup[], attacker: number, to: Defender | null): Attack[] {
  const option = attackFor(groups, attacker, to);
  return option ? [...plan.filter((a) => a.attacker !== attacker), option] : plan;
}

export function withoutAttack(plan: Attack[], attacker: number): Attack[] {
  return plan.filter((a) => a.attacker !== attacker);
}

// Every creature that can attack, those already planned where they are and the rest at `to`.
export function attackingWithAll(plan: Attack[], groups: AttackerGroup[], to: Defender | null): Attack[] {
  const planned = new Set(plan.map((a) => a.attacker));
  const added = groups.flatMap((g) => g.attackers.filter((oid) => !planned.has(oid)).map((oid) => attackFor(groups, oid, to)))
    .filter((a): a is Attack => a !== null);
  return [...plan, ...added];
}

// Where a new attacker goes unless it's dragged elsewhere: the next opponent round the table who
// can be attacked.
export function firstDefender(state: PublicState, seat: string, groups: AttackerGroup[]): Defender | null {
  const live = livePlayers(state);
  const at = live.indexOf(seat);
  const order = [...live.slice(at + 1), ...live.slice(0, Math.max(0, at))];
  const pid = order.find((p) => isAttackable(groups, { pid: p, planeswalker: null }));
  const first = groups[0]?.may_attack[0];
  return pid ? { pid, planeswalker: null } : first ? attacked(first) : null;
}

type BlockIndex = { groupOf: Map<number, BlockerGroup>; mayBlock: Map<BlockerGroup, Set<number>>; attackers: Map<number, AttackerToBlock> };

const blockIndexes = new WeakMap<BlockOptions, BlockIndex>();

function blockIndex(options: BlockOptions): BlockIndex {
  let index = blockIndexes.get(options);
  if (!index) {
    index = {
      groupOf: new Map(options.groups.flatMap((g) => g.blockers.map((oid) => [oid, g] as const))),
      mayBlock: new Map(options.groups.map((g) => [g, new Set(g.may_block)] as const)),
      attackers: new Map(options.attackers.map((a) => [a.attacker, a] as const)),
    };
    blockIndexes.set(options, index);
  }
  return index;
}

export function isBlocker(options: BlockOptions, blocker: number): boolean {
  return blockIndex(options).groupOf.has(blocker);
}

export function canBlock(options: BlockOptions, blocker: number, attacker: number): boolean {
  const index = blockIndex(options);
  const group = index.groupOf.get(blocker);
  return group !== undefined && (index.mayBlock.get(group)?.has(attacker) ?? false);
}

// 509.1a: each blocker blocks one attacker here, and any number may block the same one.
export function withBlock(plan: Block[], blocker: number, attacker: number): Block[] {
  return [...plan.filter((b) => b.blocker !== blocker), { blocker, attacker }];
}

export function withoutBlock(plan: Block[], blocker: number): Block[] {
  return plan.filter((b) => b.blocker !== blocker);
}

// What the declaration answers to beyond who may block what (509.1b-c, 702.111b), for the prompt:
// attackers that ask for more than one blocker, for no more than some, or for every creature able to
// block them, and creatures that must block or can't block alone.
export type BlockAsks = {
  needMore: { attacker: number; least: number }[];
  atMost: { attacker: number; most: number }[];
  lures: number[];
  mustBeBlocked: number[];
  mustBlock: number[];
  notAlone: number[];
};

export function blockAsks(options: BlockOptions): BlockAsks {
  return {
    needMore: options.attackers.filter((a) => a.least_blockers > 1).map((a) => ({ attacker: a.attacker, least: a.least_blockers })),
    atMost: options.attackers.flatMap((a) => {
      const most = optionValue(a.most_blockers);
      return most === null ? [] : [{ attacker: a.attacker, most }];
    }),
    lures: options.attackers.filter((a) => a.must_be_blocked_by_all_able).map((a) => a.attacker),
    mustBeBlocked: options.attackers.filter((a) => a.must_be_blocked && !a.must_be_blocked_by_all_able).map((a) => a.attacker),
    mustBlock: options.groups.filter((g) => g.must_block).flatMap((g) => g.blockers),
    notAlone: options.groups.filter((g) => g.cant_block_alone).flatMap((g) => g.blockers),
  };
}

export function damageInOrder(a: DamageAssignment, order: number[]): number[] {
  const trample = optionValue(a.trample_to) !== null;
  let left = a.power;
  const given = new Map<number, number>();
  order.forEach((oid, i) => {
    const lethal = a.blockers.find((b) => b.blocker === oid)?.lethal ?? 0;
    const n = i === order.length - 1 && !trample ? left : Math.min(left, lethal);
    given.set(oid, n);
    left -= n;
  });
  const amounts = a.blockers.map((b) => given.get(b.blocker) ?? 0);
  return trample ? [...amounts, left] : amounts;
}

export function movedInOrder(order: number[], oid: number, to: number): number[] {
  const without = order.filter((x) => x !== oid);
  return [...without.slice(0, to), oid, ...without.slice(to)];
}
